import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Container, GlassCard } from "@/components/site/Primitives";
import { FormActions } from "@/components/FormActions";
import { stockImages } from "@/lib/content";
import { submitEnquiry } from "@/lib/api/public.functions";
import { useFirmContact } from "@/lib/hooks/use-firm-contact";
import {
  formatOfficeAddress,
  googleMapsEmbedUrl,
  googleMapsLinkUrl,
} from "@/lib/firm-contact";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact KS Lau & Co — Penang" },
      { name: "description", content: "Get in touch with KS Lau & Co. Office in Penang, Malaysia. Phone, email and enquiry form." },
      { property: "og:title", content: "Contact KS Lau & Co" },
      { property: "og:description", content: "Speak with our team about tax, audit, advisory or ESG matters." },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const firmContact = useFirmContact();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const detailsVisible = message.trim().length > 0;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    setSubmitting(true);
    const fd = new FormData(form);
    const phoneCountryCode = String(fd.get("phoneCountryCode") ?? "");
    const phoneNumber = String(fd.get("phone") ?? "");
    const phone = phoneNumber ? `${phoneCountryCode} ${phoneNumber}`.trim() : "";

    try {
      await submitEnquiry({
        data: {
          name: String(fd.get("name")),
          email: String(fd.get("email")),
          company: String(fd.get("company") ?? ""),
          phone,
          designation: String(fd.get("designation") ?? ""),
          message: String(fd.get("message")),
          source: "contact",
        },
      });
      form.reset();
      setMessage("");
      toast.success("Thank you — we'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  }

  const details = [
    { icon: MapPin, label: "Office", value: formatOfficeAddress(firmContact) },
    { icon: Phone, label: "Phone", value: firmContact.phone, href: `tel:${firmContact.phone.replace(/\s/g, "")}` },
    { icon: Mail, label: "Email", value: firmContact.email, href: `mailto:${firmContact.email}` },
    { icon: Clock, label: "Hours", value: firmContact.hours },
  ];

  return (
    <>
      <section className="relative isolate overflow-hidden">
        <img
          src={stockImages.contactPenang}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-t from-primary/75 via-primary/40 to-primary/10 md:bg-gradient-to-r md:from-primary/70 md:via-primary/35 md:to-primary/5"
        />
        <Container>
          <div className="flex min-h-[32vh] flex-col justify-end py-10 text-primary-foreground md:min-h-[40vh] md:py-14">
            <h1 className="max-w-3xl text-3xl font-semibold leading-[1.15] tracking-tight md:text-4xl lg:text-5xl">
              Speak with our team.
            </h1>
          </div>
        </Container>
      </section>

      <section className="relative py-12 md:py-16 lg:py-20">
        <Container>
          <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr] lg:gap-10">
            <div className="space-y-3">
              {details.map((d, i) => (
                <GlassCard key={d.label} animateIndex={i} hover={false} className="p-5">
                  <div className="flex items-start gap-4">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-sm bg-primary/8 text-primary">
                      <d.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{d.label}</p>
                      {d.href ? (
                        <a href={d.href} className="mt-1 block whitespace-pre-line text-base font-semibold text-primary hover:underline">{d.value}</a>
                      ) : (
                        <p className="mt-1 whitespace-pre-line text-base font-semibold text-primary">{d.value}</p>
                      )}
                    </div>
                  </div>
                </GlassCard>
              ))}

              <GlassCard hover={false} className="overflow-hidden p-0">
                <iframe
                  title="KS Lau & Co office location"
                  src={googleMapsEmbedUrl(firmContact)}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="aspect-[2/1] w-full border-0"
                  allowFullScreen
                />
                <a
                  href={googleMapsLinkUrl(firmContact)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-primary/5 px-4 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-primary hover:bg-primary/10"
                >
                  <MapPin className="h-3.5 w-3.5" /> Open in Google Maps
                </a>
              </GlassCard>
            </div>

            <GlassCard hover={false} className="p-6 md:p-8">
              <h2 className="text-2xl font-semibold tracking-tight text-primary md:text-3xl">Contact Us</h2>
              <p className="mt-2 text-base text-muted-foreground">
                Share a few details and we'll respond within one business day.
              </p>

              <form onSubmit={handleSubmit} className="mt-6 grid gap-4">
                <div>
                  <Label htmlFor="message">How can we help?</Label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your question…"
                    className="mt-2 w-full rounded-sm border border-border bg-white px-4 py-3 text-base text-foreground outline-none transition focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>

                {detailsVisible && (
                  <>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field name="name" label="Preferred Name" required />
                      <Field name="email" type="email" label="Email" required />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field name="company" label="Company" required />
                      <PhoneField />
                    </div>
                    <div>
                      <Field name="designation" label="Designation" />
                    </div>
                  </>
                )}

              <FormActions>
                <button
                  type="submit"
                  disabled={submitting || !detailsVisible}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-gold bg-transparent px-6 py-4 text-base font-semibold text-gold transition hover:bg-gold/10 disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {submitting ? "Sending…" : "Send"}
                </button>
              </FormActions>
                <p className="text-sm text-muted-foreground">
                  By submitting this form you agree we may contact you in relation to your enquiry.
                </p>
              </form>
            </GlassCard>
          </div>
        </Container>
      </section>
    </>
  );
}

function Label({ children, htmlFor }: { children: React.ReactNode; htmlFor: string }) {
  return (
    <label htmlFor={htmlFor} className="text-sm font-semibold uppercase tracking-[0.12em] text-primary/80">
      {children}
    </label>
  );
}

function Field({
  name,
  label,
  type = "text",
  required,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <Label htmlFor={name}>{label}</Label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        className="mt-2 h-12 w-full rounded-sm border border-border bg-white px-4 text-base text-foreground outline-none transition focus:border-primary focus:ring-1 focus:ring-primary"
      />
    </div>
  );
}

const COUNTRY_CODES: { code: string; label: string }[] = [
  { code: "+60", label: "🇲🇾 +60" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+62", label: "🇮🇩 +62" },
  { code: "+66", label: "🇹🇭 +66" },
  { code: "+84", label: "🇻🇳 +84" },
  { code: "+63", label: "🇵🇭 +63" },
  { code: "+86", label: "🇨🇳 +86" },
  { code: "+852", label: "🇭🇰 +852" },
  { code: "+886", label: "🇹🇼 +886" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+82", label: "🇰🇷 +82" },
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+64", label: "🇳🇿 +64" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+1", label: "🇺🇸 +1" },
];

function PhoneField() {
  const [code, setCode] = useState("+60");
  return (
    <div>
      <Label htmlFor="phone">Phone</Label>
      <div className="mt-2 flex gap-2">
        <select
          name="phoneCountryCode"
          aria-label="Country code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="h-12 shrink-0 rounded-sm border border-border bg-white px-2 text-base text-foreground outline-none transition focus:border-primary focus:ring-1 focus:ring-primary"
        >
          {COUNTRY_CODES.map((c) => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
        <input
          id="phone"
          name="phone"
          type="tel"
          inputMode="tel"
          required
          placeholder="12 345 6789"
          className="h-12 w-full min-w-0 rounded-sm border border-border bg-white px-4 text-base text-foreground outline-none transition focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </div>
    </div>
  );
}
